<template>
	<div class="box">
		<div class="row">
            <div class="col d-flex">
                <h3>{{ $t('sync_groomy.title') }}</h3>
            </div>
            <div class="col-auto ml-auto d-flex">
                <b-button variant="primary" @click="add_ask_sync">
                    {{ $t('global.ajouter') }} <font-awesome-icon :icon="['fal', 'plus']" class="ml-1"/>
                </b-button>
            </div>
        </div>
		<div class="row">
			<LoadingSpinner v-if="loading" class="col-12" />
			<div v-else class="col-12 mt-2">
                <div class="row p-3">
                    <div class="col-1">
                        <h3>{{ $t('sync_groomy.status.title') }}</h3>
                    </div>
                    <div class="col-2">
                        <h3>{{ $t('sync_groomy.date_demande') }}</h3>
                    </div>
                    <div class="col-3">
                        <h3>{{ $t('sync_groomy.sync_type') }}</h3>
                    </div>
                    <div class="col-2">
                        <h3>{{ $t('sync_groomy.horse_sync') }}</h3>
                    </div>
                    <div class="col-1">
                        <h3>{{ $t('sync_groomy.echeance') }}</h3>
                    </div>
                    <div class="col-2">
                        <h3>{{ $t('sync_groomy.date_reponse') }}</h3>
                    </div>
                    <div class="col-1"></div>
                </div>
				<div class="trigger p-3 row" v-for="sync in syncs" :key="sync.synclicence_id">
                    <div class="col-1">
                        <b-badge v-if="sync.synclicence_status == 'ask'" pill class="p-2" variant="warning">
                            <font-awesome-icon class="mr-1" :icon="['fal', 'clock']"/> {{ $t('sync_groomy.status.ask') }}
                        </b-badge>
                        <b-badge v-if="sync.synclicence_status == 'accept'" pill class="p-2" variant="success">
                            <font-awesome-icon class="mr-1" :icon="['fal', 'check']"/> {{ $t('sync_groomy.status.accept') }}
                        </b-badge>
                        <b-badge v-if="sync.synclicence_status == 'refuse'" pill class="p-2" variant="danger">
                            <font-awesome-icon class="mr-1" :icon="['far', 'times']"/> {{ $t('sync_groomy.status.refuse') }}
                        </b-badge>
                        <b-badge v-if="sync.synclicence_status == 'cancel'" pill class="p-2" variant="danger">
                            <font-awesome-icon class="mr-1" :icon="['far', 'times']"/> {{ $t('sync_groomy.status.cancel') }}
                        </b-badge>
                    </div>
                    <div class="col-2">
                        {{ sync.synclicence_inserted }}
                    </div>
                    <div class="col-3">
                        {{ $t('sync_groomy.types.'+sync.type.synctype_code) }}
                    </div>
                    <div class="col-2">
                        <span v-if="sync.horses && sync.horses.length > 0">
                            <span v-for="(horse, index) in sync.horses" :key="horse.horse_id || index">
                                {{ horse.horse_nom || horse }}<br v-if="index < sync.horses.length - 1">
                            </span>
                        </span>
                        <span v-else>-</span>
                    </div>
                    <div class="col-1">
                        {{ sync.synclicence_end }}
                    </div>
                    <div class="col-2">
                        {{ sync.synclicence_response_date }}
                    </div>
                    <div class="col-1">
                        <b-button v-if="sync.synclicence_status == 'accept'" variant="secondary" @click="openConfirm('cancel', sync.synclicence_id)">
                            {{ $t('sync_groomy.action.cancel.ok') }} <font-awesome-icon class="mr-1" :icon="['far', 'times']"/> 
                        </b-button>
                    </div>
                </div>
			</div>
		</div>

		<b-modal ref="modal" hide-footer>
            <template v-slot:modal-title>
                {{ $t("sync_groomy.ajout_sync") }}
            </template>

            <div class="row">
                <div class="col-12">
                    <label>{{ $t('sync_groomy.sync_type') }}</label>
                    <e-select
                        v-model="sync_type_selected"
                        track-by="synctype_code"
                        label="synctype_code"
                        :options="sync_types"
                        :allow-empty="true"
                        :sortable="false"
                        :loading="loading"
                    >
		                <template slot="option" slot-scope="{ option }">{{ $t('sync_groomy.types.'+option.synctype_code) }}</template>
		                <template slot="singleLabel" slot-scope="{ option }">{{ $t('sync_groomy.types.'+option.synctype_code) }}</template>
		                <template slot="noOptions">{{ $t('global.list_empty') }}</template>
                    </e-select>
                </div>
                <div v-if="sync_type_selected" class="col-12 py-2 my-2">
                    <span v-html="$t('sync_groomy.modal_text.'+sync_type_selected.synctype_code)"></span>
                </div>
                <div v-if="sync_type_selected && sync_type_selected.synctype_horse" class="col-12 mt-1">
                    <label>{{ $t('sync_groomy.horse_sync') }}</label>
                    <SearchHorse :horse_selected.sync="horse_selected" />
                    <button 
                        v-for="(horse, index) in horses"
                        :key="'horse_'+index"
                        @click="deleteHorse(horse)"
                        class="btn btn-sm btn-primary rounded-pill mt-1 mr-2"
                    >
                        {{ horse.horse_nom }} 
                        <font-awesome-icon :icon="['fal', 'times']" />
                    </button>
                </div>
                <div class="col-12 text-center">
                	<b-button class="mt-3" :disabled="!sync_type_selected" variant="primary" @click.prevent="checkForm">{{ $t("global.valider") }} <font-awesome-icon v-if="processing" :icon="['fas', 'spinner']" pulse /><font-awesome-icon v-else :icon="['fal', 'check']"/></b-button>
                </div>
            </div>
        </b-modal>

        <ModalConfirm
            ref="modal_confirm"
            :text_title="action.trad_title"
            :text_question="action.trad_question"
            :text_message="action.trad_message"
            :text_button_ok="action.trad_ok"
            :text_button_cancel="getTrad('global.close')"
            :icon="action.icon"
            :callback_validation="confirmAction"
            :processing="processing"
        />
	</div>
</template>

<script type="text/javascript">
	import Tiers from "@/mixins/Tiers.js"

    export default {
        name: 'SyncGroomy',
        mixins: [Tiers],
        props: ['tiers_id'],
        data() {
            return {
                loading: false,
                syncs: [],
                sync_types: [],
                sync_type_selected: null,
                processing: false,
                action: {
                    trad_title: '',
                    trad_question: '',
                    trad_message: '',
                    trad_ok: '',
                    icon: '',
                    callback: null
                },
                synclicence_edit: 0,
                horses: [],
                horse_selected: null
            }
        },
        mounted () {
            this.init_component()
        },
        methods:{
            async init_component() {
                this.loading = true
                const has_groomy = await this.hasGroomy(this.tiers_id)

                if(!has_groomy) {
                	this.failureToast('sync_groomy.tiers_hasnt_groomy')
                	this.$router.push({ name: 'tiersFiche', params: { tiers_id: this.tiers_id }})
                	return false
                }

                const syncs = await this.getDemandeSynchroByTiers(this.tiers_id)
                this.syncs = syncs.map(sync => ({
                	...sync,
                	synclicence_inserted: this.formatDate(sync.synclicence_inserted),
					synclicence_end: sync.synclicence_end ? this.formatDate(sync.synclicence_end) : '',
					synclicence_response_date: sync.synclicence_response_date ? this.formatDate(sync.synclicence_inserted) : ''
                }))

                if(this.sync_types.length == 0) {
	            	this.sync_types = await this.getSyncTypesByTiers(this.tiers_id)
                }
                this.loading = false
            },

            add_ask_sync() {
            	this.sync_type_selected = null
            	this.$refs.modal.show()
            },

            async checkForm() {
            	if(!this.sync_type_selected || this.processing)
            		return false
                const horses = this.sync_type_selected.synctype_horse ? this.horses.map(horse => ({horse_id: horse.horse_id, horse_nom: horse.horse_nom})) : []
            	this.processing = true
            	await this.addDemandeSynchroByTiers(this.tiers_id, this.sync_type_selected.synctype_id, horses)
            	this.processing = false
            	this.$refs.modal.hide()
            	this.init_component()
            },

            async confirmAction() {
                this.processing = true
                await this.actionSyncGroomy(this.synclicence_edit, this.action.action)
                this.processing = false
                this.$refs.modal_confirm.closeModal()
                this.action = {
                    trad_title: '',
                    trad_question: '',
                    trad_message: '',
                    trad_ok: '',
                    icon: '',
                    callback: null
                }
                this.init_component()
            },

            openConfirm(action, synclicence_id) {
                this.synclicence_edit = synclicence_id
                this.action = {
                    action: action,
                    trad_title: this.getTrad('sync_groomy.action.'+action+'.title'),
                    trad_question: this.getTrad('sync_groomy.action.'+action+'.question'),
                    trad_message: this.getTrad('sync_groomy.action.'+action+'.message'),
                    trad_ok: this.getTrad('sync_groomy.action.'+action+'.ok'),
                    icon: this.getTrad('sync_groomy.action.'+action+'.icon').split(',')
                }
                this.$refs.modal_confirm.openModal()
            },

            deleteHorse(horse) {
                const index = this.horses.indexOf(horse);
                if (index > -1) {
                    this.horses.splice(index, 1)
                }
            }
        },

        watch: {
            horse_selected(val) {
                if(val) {
                    this.horses.push(val)
                    this.horse_selected = null
                }
            }
        },

        components: {
        	LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35'),
            ModalConfirm: () => import('GroomyRoot/components/Modals/ModalConfirm'),
            SearchHorse : () => import('@/components/Contract/SearchHorse')
        }
    }
</script>